/**
 * ImportCustomersWizard - Orquestrador do wizard de importação em massa de CLIENTES.
 *
 * Estrutura espelha o ImportDealsWizard, mas com 5 etapas (sem step de
 * "Mapeamento + Defaults" complexo):
 *   1. Upload CSV                (Step1UploadCustomers)
 *   2. Mapeamento de colunas     (Step2MappingCustomers — exige nome + telefone + cpf/cnpj)
 *   3. Defaults & Opções         (Step3DefaultsCustomers — silent mode, DDI)
 *   4. Validação (transição)     (Step4Validating — reutilizado dos deals)
 *   5. Aprovação explícita       (Step5ApprovalCustomers)
 *   6. Progresso & Resultado     (Step6ProgressCustomers)
 *
 * Layout "Premium Wizard" (max-w-5xl + sidebar verde + resumo dinâmico)
 * padronizado com ImportDealsWizard e ImportListWizard.
 *
 * RELEASE: 2026-05-17 v1.0.0 - Versão inicial (Fase 2)
 */

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { parseCSV, type CSVRow } from '@/lib/csv-utils';
import { useCustomerImport } from '@/hooks/useCustomerImport';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  Loader2, X, ChevronLeft, Check, Upload, Columns3, Settings2, CheckCircle2,
  ShieldCheck, Activity, UserCheck, ListChecks, CheckCheck, AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

import { Step1UploadCustomers } from './steps/Step1UploadCustomers';
import { Step2MappingCustomers } from './steps/Step2MappingCustomers';
import { Step3DefaultsCustomers } from './steps/Step3DefaultsCustomers';
import { Step4Validating } from '@/components/crm/import/steps/Step4Validating';
import { Step5ApprovalCustomers } from './steps/Step5ApprovalCustomers';
import { Step6ProgressCustomers } from './steps/Step6ProgressCustomers';
import { CUSTOMER_FIELDS, type ValidationCounters } from './steps/types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onComplete?: () => void;
  /** Retomar job existente direto no step 5 ou 6 (vindo da listagem). */
  resume?: { jobId: string; step: 5 | 6 } | null;
}

const STEPS = [
  { key: 'upload',     label: 'Upload',       icon: Upload,        description: 'Selecione o arquivo CSV de clientes' },
  { key: 'mapping',    label: 'Mapeamento',   icon: Columns3,      description: 'Mapeie as colunas do CSV' },
  { key: 'defaults',   label: 'Configuração', icon: Settings2,     description: 'Opções de importação' },
  { key: 'validating', label: 'Validação',    icon: CheckCircle2,  description: 'Verificação server-side' },
  { key: 'approval',   label: 'Aprovação',    icon: ShieldCheck,   description: 'Revise e aprove a importação' },
  { key: 'progress',   label: 'Progresso',    icon: Activity,      description: 'Acompanhe o processamento' },
] as const;

const logger = createLogger('ImportCustomersWizard');

export function ImportCustomersWizard({ open, onOpenChange, onComplete, resume }: Props) {
  const { toast } = useToast();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const importApi = useCustomerImport();

  // ===== Estado do wizard =====
  const [step, setStep] = useState(1);
  const [jobName, setJobName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<CSVRow[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [silentMode, setSilentMode] = useState(true);
  const [defaultCountryCode, setDefaultCountryCode] = useState('55');
  const [jobId, setJobId] = useState<string | null>(null);
  const [validationCounters, setValidationCounters] = useState<ValidationCounters | null>(null);
  const [validationProgress, setValidationProgress] = useState<ValidationCounters | null>(null);
  const [isResumed, setIsResumed] = useState(false);
  const [confirmCancelInWizard, setConfirmCancelInWizard] = useState(false);

  const currentStepIndex = step - 1;
  const currentStepData = STEPS[currentStepIndex];
  const StepIcon = currentStepData.icon;

  // Reset ao fechar.
  useEffect(() => {
    if (!open) {
      setStep(1);
      setJobName('');
      setHeaders([]);
      setRows([]);
      setMapping({});
      setJobId(null);
      setValidationCounters(null);
      setSilentMode(true);
      setDefaultCountryCode('55');
      setIsResumed(false);
    }
  }, [open]);

  // Resume: hidrata estado a partir de get_customer_import_job_summary
  useEffect(() => {
    if (!open || !resume || !tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_customer_import_job_summary', {
          p_job_id: resume.jobId,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        if (cancelled || !row) return;
        setJobId(row.id);
        setJobName(row.name ?? '');
        setValidationCounters({
          total_rows: row.total_rows ?? 0,
          valid_count: row.valid_count ?? 0,
          invalid_count: row.invalid_count ?? 0,
          duplicate_count: row.duplicate_count ?? 0,
          blacklisted_count: row.blacklisted_count ?? 0,
        });
        setIsResumed(true);
        setStep(resume.step);
      } catch (err: any) {
        logger.error('Falha ao retomar job de importação de clientes', { jobId: resume.jobId, error: err?.message });
        toast({
          title: 'Não foi possível abrir o job',
          description: err?.message ?? 'Erro desconhecido — feche e tente novamente.',
          variant: 'destructive',
          duration: 8000,
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resume?.jobId, resume?.step, tenantId]);

  // Polling do job no step 6
  const jobDetails = importApi.useJobDetails(step === 6 ? jobId : null);

  // ============= Handlers =============

  async function handleFile(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Arquivo muito grande', description: 'Máximo 10MB', variant: 'destructive' });
      return;
    }
    const text = await file.text();
    // v1.x (2026-05-18): requiredColumns=[] — clientes não exigem "telefone"
    // no parser; o mapeamento e a RPC validate_customer_import_job cuidam disso.
    const result = parseCSV(text, { requiredColumns: [] });
    if (result.errors.length) {
      toast({ title: 'Erro no CSV', description: result.errors[0], variant: 'destructive', duration: 10000 });
      return;
    }
    if (result.totalRows === 0) {
      toast({ title: 'CSV vazio', variant: 'destructive', duration: 10000 });
      return;
    }
    if (result.totalRows > 50000) {
      toast({ title: 'Limite excedido', description: 'Máx. 50.000 linhas', variant: 'destructive', duration: 10000 });
      return;
    }

    setHeaders(result.headers);
    setRows(result.rows);
    if (!jobName) setJobName(file.name.replace(/\.[^.]+$/, ''));

    // Autodetect de mapeamento
    const { AUTODETECT } = await import('./steps/types');
    const m: Record<string, string> = {};
    for (const h of result.headers) {
      const f = AUTODETECT[h.toLowerCase().trim()];
      if (f) m[f] = h;
    }
    setMapping(m);
    setStep(2);
  }

  async function handleValidate() {
    if (rows.length === 0 || headers.length === 0) {
      toast({
        title: 'Reenvie o CSV para validar novamente',
        description:
          'Esta importação foi aberta a partir de um job já validado e o arquivo original não está mais em memória.',
        variant: 'destructive',
        duration: 12000,
      });
      return;
    }
    // Required: nome + telefone + (cpf OU cnpj)
    const missingRequired = CUSTOMER_FIELDS
      .filter((f) => f.required && !mapping[f.key])
      .map((f) => f.label.replace(/\s*\(obrigatório.*?\)\s*\*?/i, '').trim());
    if (missingRequired.length > 0) {
      toast({
        title: 'Campos obrigatórios não mapeados',
        description: `Mapeie a(s) coluna(s): ${missingRequired.join(', ')}`,
        variant: 'destructive',
      });
      setStep(2);
      return;
    }
    if (!mapping.cpf && !mapping.cnpj) {
      toast({
        title: 'Mapeie CPF ou CNPJ',
        description: 'Cliente exige pelo menos um documento (CPF para PF ou CNPJ para PJ).',
        variant: 'destructive',
      });
      setStep(2);
      return;
    }

    let createdJobId: string | null = null;
    try {
      setStep(4);
      setValidationProgress(null);

      const newJobId = await importApi.createJob.mutateAsync({
        name: jobName || 'Importação sem nome',
        mapping,
        defaults: { duplicate_behavior: 'skip' },
        options: {
          silent_mode: silentMode,
          default_country_code: defaultCountryCode || '55',
        },
        total_rows: rows.length,
      });
      createdJobId = newJobId;
      setJobId(newJobId);

      const payload = rows.map((row, idx) => ({ row_index: idx, raw_row: row }));
      await importApi.populateItems.mutateAsync({ jobId: newJobId, rows: payload });

      const counters = await importApi.validateJob.mutateAsync({
        jobId: newJobId,
        onProgress: (c) => setValidationProgress(c),
      });
      setValidationCounters(counters);
      setStep(5);
    } catch (err: any) {
      logger.error('Falha na validação do job de importação de clientes', {
        message: err?.message, code: err?.code, jobId,
      });
      const isTimeout = err?.code === '57014';
      const populated = !!createdJobId;
      toast({
        title: isTimeout
          ? 'Validação demorou demais — vamos tentar de novo'
          : jobId
            ? 'Falha na validação — clique em Validar novamente'
            : 'Erro na validação',
        description: isTimeout
          ? 'O servidor processa em lotes. Clique em Validar novamente: a importação continua de onde parou.'
          : err?.message ?? 'Falha desconhecida ao validar',
        variant: 'destructive',
        duration: 12000,
      });
      if (!populated && (createdJobId ?? jobId)) {
        const orphanId = createdJobId ?? jobId;
        if (orphanId) {
          try { await importApi.cancelJob.mutateAsync(orphanId); } catch { /* noop */ }
          setJobId(null);
        }
      }
      setStep(3);
    }
  }

  async function handleApprove() {
    if (!jobId) {
      toast({
        title: 'Job ainda não carregado',
        description: 'Aguarde alguns segundos e tente novamente.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const res = await importApi.approveAndEnqueue.mutateAsync(jobId);
      toast({
        title: 'Importação iniciada',
        description: `${res.batches_created} lotes • ${res.items_enqueued} clientes na fila`,
      });
      setStep(6);
    } catch (err: any) {
      logger.error('Falha ao aprovar/enfileirar importação de clientes', {
        message: err?.message, code: err?.code, jobId,
      });
      toast({
        title: 'Erro ao confirmar importação',
        description: err?.message ?? 'Falha desconhecida ao aprovar',
        variant: 'destructive',
        duration: 8000,
      });
    }
  }

  async function handleCancel() {
    if (jobId && step < 6 && !isResumed) {
      try { await importApi.cancelJob.mutateAsync(jobId); } catch { /* noop */ }
    }
    onOpenChange(false);
  }

  async function handleBackFromApproval() {
    if (isResumed) {
      toast({
        title: 'Não é possível voltar nesta importação',
        description: 'Este job já foi validado em outra sessão. Cancele na listagem e inicie uma nova a partir do CSV.',
        variant: 'destructive',
        duration: 10000,
      });
      return;
    }
    if (jobId) {
      try { await importApi.cancelJob.mutateAsync(jobId); } catch { /* noop */ }
    }
    setJobId(null);
    setValidationCounters(null);
    setStep(3);
  }

  function handleStepClick(targetIndex: number) {
    if (targetIndex >= currentStepIndex) return;
    if (targetIndex === 3) return; // step 4 = transição
    setStep(targetIndex + 1);
  }

  const isBusy =
    importApi.createJob.isPending ||
    importApi.populateItems.isPending ||
    importApi.validateJob.isPending ||
    importApi.approveAndEnqueue.isPending;

  // ============= Render =============

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleCancel(); else onOpenChange(true); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">

          {/* ===== SIDEBAR PREMIUM (verde — diferencia do wizard de negócios azul) ===== */}
          <aside
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, hsl(155, 60%, 15%) 0%, hsl(160, 55%, 20%) 50%, hsl(165, 50%, 25%) 100%)',
            }}
          >
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            <div className="relative z-10 flex flex-col h-full p-5">
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <UserCheck className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">Importar Clientes</h2>
                <p className="text-sm text-white/60 mt-1">
                  Etapa {currentStepIndex + 1} de {STEPS.length}
                </p>
              </div>

              <nav className="flex-1 space-y-1">
                {STEPS.map((s, index) => {
                  const isCompleted = index < currentStepIndex;
                  const isActive = index === currentStepIndex;
                  const isPending = index > currentStepIndex;
                  const Icon = s.icon;
                  const isClickable = isCompleted && index !== 3 && !isBusy;

                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => isClickable && handleStepClick(index)}
                      disabled={!isClickable && !isActive}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all duration-200',
                        isActive && 'bg-white/10 border border-white/20',
                        isClickable && 'hover:bg-white/5 cursor-pointer',
                        isPending && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      <div
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all',
                          isCompleted && 'bg-green-500 border-green-500',
                          isActive && 'bg-white border-white',
                          isPending && 'bg-transparent border-white/30',
                        )}
                      >
                        {isCompleted ? (
                          <Check className="h-4 w-4 text-white" />
                        ) : (
                          <Icon className={cn('h-4 w-4', isActive ? 'text-primary' : 'text-white/60')} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={cn('block text-sm font-medium truncate', isActive ? 'text-white' : 'text-white/80')}>
                          {s.label}
                        </span>
                        <span className="block text-xs text-white/40 truncate">{s.description}</span>
                      </div>
                    </button>
                  );
                })}
              </nav>

              <div className="mt-auto pt-4 border-t border-white/10">
                <h3 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-3">Resumo</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ListChecks className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Linhas</span>
                    </div>
                    <span className="text-sm font-medium text-white">{rows.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCheck className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Válidos</span>
                    </div>
                    <span
                      className={cn(
                        'text-sm font-medium',
                        (validationCounters?.valid_count ?? 0) > 0 ? 'text-green-400' : 'text-white',
                      )}
                    >
                      {validationCounters?.valid_count ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Problemas</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {(validationCounters?.invalid_count ?? 0) + (validationCounters?.duplicate_count ?? 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* ===== CONTEÚDO PRINCIPAL ===== */}
          <main className="flex flex-col h-full min-h-0 overflow-hidden">
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 text-left">
                  <DialogTitle className="text-lg font-semibold">{currentStepData.label}</DialogTitle>
                  <DialogDescription className="text-sm">{currentStepData.description}</DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {step <= 4 ? (
              <div className="flex-1 min-h-0 min-w-0 overflow-y-auto">
                <div className="p-6 pb-8">
                  {step === 1 && (
                    <Step1UploadCustomers
                      jobName={jobName}
                      onJobNameChange={setJobName}
                      onFileSelected={handleFile}
                    />
                  )}
                  {step === 2 && (
                    <Step2MappingCustomers
                      rowCount={rows.length}
                      headers={headers}
                      mapping={mapping}
                      onMappingChange={setMapping}
                    />
                  )}
                  {step === 3 && (
                    <Step3DefaultsCustomers
                      silentMode={silentMode}
                      defaultCountryCode={defaultCountryCode}
                      onSilentModeChange={setSilentMode}
                      onDefaultCountryCodeChange={setDefaultCountryCode}
                    />
                  )}
                  {step === 4 && (
                    <Step4Validating progress={validationProgress} totalRows={rows.length} />
                  )}
                </div>
              </div>
            ) : (
              <div
                className={
                  step === 5
                    ? 'flex-1 min-h-0 min-w-0 overflow-y-auto p-6 flex flex-col'
                    : 'flex-1 min-h-0 min-w-0 overflow-hidden p-6 flex flex-col'
                }
              >
                {step === 5 && validationCounters && (
                  <Step5ApprovalCustomers counters={validationCounters} jobId={jobId} />
                )}
                {step === 6 && (
                  <Step6ProgressCustomers
                    isLoading={jobDetails.isLoading}
                    details={jobDetails.data}
                    jobId={jobId}
                    error={jobDetails.error}
                    onCancel={jobId ? () => setConfirmCancelInWizard(true) : undefined}
                    cancelPending={importApi.cancelJob.isPending}
                  />
                )}
              </div>
            )}

            <div className="flex-shrink-0 bg-background px-6 py-4 border-t flex justify-between items-center gap-2">
              <Button
                variant="outline"
                onClick={
                  step === 1 || step >= 6
                    ? handleCancel
                    : step === 5
                      ? handleBackFromApproval
                      : () => setStep((s) => s - 1)
                }
                disabled={isBusy || step === 4 || (step === 5 && isResumed)}
                title={
                  step === 5 && isResumed
                    ? 'Este job foi aberto a partir da listagem e não pode ser editado sem reenviar o CSV'
                    : undefined
                }
              >
                {step === 1 || step >= 6 ? (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    {step >= 6 ? 'Fechar' : 'Cancelar'}
                  </>
                ) : (
                  <>
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Voltar
                  </>
                )}
              </Button>

              <div className="flex md:hidden items-center gap-1">
                {STEPS.map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      'h-2 rounded-full transition-all',
                      index < currentStepIndex && 'bg-green-500 w-2',
                      index === currentStepIndex && 'bg-primary w-4',
                      index > currentStepIndex && 'bg-muted w-2',
                    )}
                  />
                ))}
              </div>

              <div className="flex gap-2">
                {step === 2 && (
                  <Button
                    onClick={() => setStep(3)}
                    disabled={
                      CUSTOMER_FIELDS.some((f) => f.required && !mapping[f.key]) ||
                      (!mapping.cpf && !mapping.cnpj)
                    }
                  >
                    Próximo
                  </Button>
                )}

                {step === 3 && (
                  <Button onClick={handleValidate} disabled={isBusy}>
                    {isBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Validar
                  </Button>
                )}

                {step === 5 && validationCounters && validationCounters.valid_count > 0 && (
                  <Button
                    onClick={handleApprove}
                    disabled={!jobId || importApi.approveAndEnqueue.isPending}
                  >
                    {importApi.approveAndEnqueue.isPending && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Confirmar importação de {validationCounters.valid_count.toLocaleString('pt-BR')} clientes
                  </Button>
                )}

                {step === 6 && jobDetails.data?.job?.status === 'completed' && (
                  <Button onClick={() => { onComplete?.(); onOpenChange(false); }}>Concluir</Button>
                )}
              </div>
            </div>
          </main>
        </div>
      </DialogContent>

      <AlertDialog open={confirmCancelInWizard} onOpenChange={setConfirmCancelInWizard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar importação?</AlertDialogTitle>
            <AlertDialogDescription>
              A importação <strong>{jobName || 'em andamento'}</strong> será marcada como cancelada.
              O lote em processamento no momento termina normalmente; os lotes
              seguintes serão pulados. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={importApi.cancelJob.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                if (!jobId) return;
                try {
                  await importApi.cancelJob.mutateAsync(jobId);
                  toast({ title: 'Importação cancelada' });
                  setConfirmCancelInWizard(false);
                } catch (err: any) {
                  toast({
                    title: 'Erro ao cancelar',
                    description: err?.message ?? 'Tente novamente.',
                    variant: 'destructive',
                  });
                }
              }}
              disabled={importApi.cancelJob.isPending || !jobId}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {importApi.cancelJob.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Cancelar importação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
